import { UrlHelper } from "../core/html/url-helper";
import { UploadFileTemplate } from "../templates/upload-file-template";
import { UploadLogoTemplate } from "../templates/upload-logo-template";
import { DllNameTemplate } from "../templates/dll-name-template";
import { PanelToggleGroupVm } from "../panels/panel-toggle-group-vm";
import { Logger } from "../logger/logger";
import { ColorQrCode } from "../checkresultdialog/color-qr-code";
import { ResultLevel } from "../checkresultdialog/checkresultdata";
import { KoExtend } from "../extend/knockout-extend";
import { SqafSettingStorage } from "../core/settings/sqaf-setting-storage";
import { NautilusFeatureName } from "../types/NautilusFeatureName";
import { SqafFeatureCheckService } from "../core/settings/sqaf-feature-check-service";

ko.bindingHandlers.qrcode = {
	update: async (element, valueAccessor) => {
		let currentMessages: KnockoutComputed<string> = valueAccessor() as any;
		let message: string = currentMessages();

		const maxLength = 1024;
		if (message.length > maxLength) {
			message = message.substring(0, maxLength);
		}

		$(element).show();
		if (!await ColorQrCode.encodeMessageAsync(element, message).catch((err) => { Logger.error(err); return false; })) {
			$(element).hide();
		}
	}
};

ko.bindingHandlers.sqafFileUpload = {
	update: (element, valueAccessor) => {
		let uploadFileTemplate: UploadFileTemplate = valueAccessor();

		let $elem = $(element);
		if (uploadFileTemplate) {
			if (!uploadFileTemplate.options.disabled()) {
				$elem.removeAttr("disabled");
				$elem.siblings("label").removeClass("dx-state-disabled");
			}
		} else {
			$elem.css("disabled", "disabled");
			$elem.siblings("label").addClass("dx-state-disabled");
		}

		$elem.attr("accept", uploadFileTemplate.fileFormats());
		$elem.on("change", uploadFileTemplate.uploadFile);
	}
};

//SqafImageUpload binding handler for uploading an image and set the valueAccessor to uploaded file id
ko.bindingHandlers.sqafimageupload = {
	update: (element, valueAccessor) => {
		let koValue = valueAccessor();
		let template = new UploadLogoTemplate(koValue);
		$(element).on("change", template.uploadFile);
	}
};

ko.bindingHandlers.dllNameCheck = {
	update: async (element, valueAccessor) => {
		let koValue = valueAccessor();
		let value = koValue();
		let $elem = $(element);
		let result = await DllNameTemplate.checkIfDllExists(value);
		let classes = result ? "fa fa-check nautilus-color-green" : "fa fa-times";
		let tooltip = result ? "Dll existiert auf dem Server" : "Dll existiert nicht auf dem Server";
		$elem.children("i").removeClass().addClass(classes).attr("title", tooltip);
	}
};

// Toggle group binding for stretching not collapsed panels
ko.bindingHandlers.toggleGroup = {
	init(element, valueAccessor, allBindings, viewModel, bindingContext) {
		let groupName: string = valueAccessor();
		PanelToggleGroupVm.getInstance(groupName).registerPanel(viewModel);
	}
};

/**
* Custom binding handler to slowly fade in/out elements
*/
ko.bindingHandlers.fadeOutVisible = {
	update: (element, valueAccessor) => {
		let value = valueAccessor();
		ko.unwrap(value) ? $(element).fadeIn(200) : $(element).fadeOut(200);
	}
};

let extractTitle = (item: ITitle | string | number, defaultLevel: ResultLevel) => {
	let title: ITitle;
	if (typeof item === "string") {
		title = { message: item };
	} else if (typeof item === "number") {
		title = { message: "", severity: item };
	} else {
		title = item || { message: "" };
	}
	title.severity = title.severity || defaultLevel;
	return title;
};

ko.bindingHandlers.title = {
	update: (element, valueAccessor, allBindings, viewModel) => {
		$(element)
			.removeAttr("data-title-info")
			.removeAttr("data-title-warning")
			.removeAttr("data-title-error");

		let title = extractTitle(valueAccessor(), ResultLevel.Info);
		if (title.message) {
			switch (title.severity) {
				case ResultLevel.Info:
					$(element).attr("data-title-info", title.message);
					break;
				case ResultLevel.Warning:
					$(element).attr("data-title-warning", title.message);
					break;
				case ResultLevel.Error:
					$(element).attr("data-title-error", title.message);
					break;
				default:
					if (title) {
						Logger.warn(`Cannot display message '${title.message}' because severity ${title.severity} is invalid`);
					}
			}
		}
	}
};

ko.bindingHandlers.severity = {
	update: (element, valueAccessor, allBindings, viewModel) => {
		let title = extractTitle(valueAccessor(), ResultLevel.None);

		if (title.severity === ResultLevel.None) {
			$(element).removeClass("check-result-icon");
			$(element).removeAttr("data-check-result-level");
		} else {
			$(element)
				.addClass("check-result-icon")
				.attr("data-check-result-level", title.severity);
		}
	}
};

export interface ITitle {
	message: string;
	severity?: ResultLevel;
}

// used for conditional required labels
ko.bindingHandlers.sqtLabelRequired = {
	update: (element, valueAccessor, allBindings, viewModel) => {
		let isRequired = KoExtend.resolve(valueAccessor());
		if (isRequired) {
			$(element).addClass("nautilus-label-required");
		} else {
			$(element).removeClass("nautilus-label-required");
		}
	}
};

ko.bindingHandlers.remarkTextHtml = {
	init: () => {
		return { "controlsDescendantBindings": true };
	},
	update: (element, valueAccessor) => {
		// https://github.com/knockout/knockout/blob/cb9252560985033ec3026d5184a15309dd8a272b/src/binding/defaultBindings/text.js
		// https://github.com/knockout/knockout/blob/cb9252560985033ec3026d5184a15309dd8a272b/src/binding/defaultBindings/html.js
		ko.utils.setHtml(element, valueAccessor());
	}
};

ko.bindingHandlers.remarkText = {
	init: () => {
		return { "controlsDescendantBindings": true };
	},
	update: (element, valueAccessor) => {
		// https://github.com/knockout/knockout/blob/cb9252560985033ec3026d5184a15309dd8a272b/src/binding/defaultBindings/text.js
		// https://github.com/knockout/knockout/blob/cb9252560985033ec3026d5184a15309dd8a272b/src/binding/defaultBindings/html.js
		ko.utils.setTextContent(element, valueAccessor());
	}
};

ko.bindingHandlers.diffColor = {
	update: (element: any, valueAccessor: any) => {
		if (!valueAccessor) {
			return;
		}

		let value = valueAccessor();

		if (value == null) {
			return;
		}

		if (typeof value === "number") {
			if (value > 0) {
				// green
				$(element).addClass("nautilus-color-green");
			} else if (value === 0) {
				// orange?
				$(element).addClass("nautilus-color-orange");
			} else {
				// red
				$(element).addClass("nautilus-color-red");
			}
		} else if (typeof value === "string") {
			$(element).addClass("nautilus-color-orange");
		}
	}
}